import {
    Box,
    Button,
    DialogContent,
    TextField,
    DialogTitle,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { Controller, useForm } from 'react-hook-form';
import rgba from 'assets/theme/function/rgba';
import SuccessSnackbarEffect from 'component/success-snackbar';
import { CreateCategoryRequest } from 'types/toont-tint/toon-tint';
import { useSaveToonTintCategoryMutation } from 'store/slices/toon-tint/toon-tint';
import Select from '@mui/material/Select';
import typography from 'assets/theme/base/typography';
import DialogIconClose from 'component/dialog-icon-close';

interface Props {
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: any;
    cardId?: string;
}

const defaultSelected = [
    { label: 'True', value: 'true' },
    { label: 'False', value: 'false' },
];

const AddCategory: React.FC<Props> = (props: Props) => {
    const { setOpenDialog, refetch, cardId } = props;

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
    } = useForm<CreateCategoryRequest>({
        defaultValues: {
            categoryPosition: 1,
            itemZIndex: 0,
            defaultSelected: 'false',
            isVisible: 'true',
        },
    });

    const [saveCategoryToonTint, { error, isError, isSuccess, isLoading }] =
        useSaveToonTintCategoryMutation();
    const onSubmit = (values: CreateCategoryRequest) => {
        saveCategoryToonTint({
            ...values,
            defaultSelected:
                values.defaultSelected === 'true'
                    ? true
                    : values.defaultSelected === 'false'
                      ? false
                      : '',
            isVisible:
                values.isVisible === 'true'
                    ? true
                    : values.isVisible === 'false'
                      ? false
                      : '',
            cardId,
        });
    };

    const formRef = useRef<HTMLFormElement>(null);

    const dispatch = useAppDispatch();
    const closeModal = () => {
        setOpenDialog(false);
        reset();
    };

    useEffect(() => {
        if (isSuccess) {
            refetch();
            closeModal();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [isError]);

    return (
        <>
            <Box
                display="flex"
                justifyContent="center"
                bgcolor={rgba(colors.primary.main, 0.5)}
            >
                <DialogTitle
                    fontSize={typography.size?.xxxxl}
                    fontWeight={typography.fontWeightXBold}
                    textTransform="uppercase"
                >
                    ADD NEW CATEGORY
                </DialogTitle>
                <DialogIconClose handleClose={closeModal} />
            </Box>

            <DialogContent>
                <Box
                    ref={formRef}
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Box display={'flex'} pt={pxToRem(15)}>
                        <Controller
                            name="categoryPosition"
                            control={control}
                            rules={{
                                required: 'Is required',
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    sx={{ ml: pxToRem(10) }}
                                    fullWidth
                                    name={name}
                                    value={value ?? ''}
                                    onChange={(e) =>
                                        onChange(Number(e.target.value) || '')
                                    }
                                    label="Position Category"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                />
                            )}
                        />

                        <Controller
                            name="itemZIndex"
                            control={control}
                            rules={{
                                required: 'Is required',
                            }}
                            render={({ field: { name, value, onChange } }) => (
                                <TextField
                                    sx={{ ml: pxToRem(10) }}
                                    fullWidth
                                    name={name}
                                    value={value ?? 0}
                                    onChange={(e) =>
                                        onChange(Number(e.target.value))
                                    }
                                    label="Item Z_Index"
                                    error={Boolean(errors[name])}
                                    helperText={
                                        errors[name]?.message?.toString() || ''
                                    }
                                />
                            )}
                        />

                        <FormControl
                            error={Boolean(errors?.defaultSelected)}
                            fullWidth
                            sx={{ ml: pxToRem(10) }}
                        >
                            <InputLabel>Default Selected</InputLabel>
                            <Controller
                                name="defaultSelected"
                                control={control}
                                rules={{
                                    required: 'Is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Default Selected"
                                        name={name}
                                        value={value || ''}
                                        onChange={(e) =>
                                            onChange(String(e.target.value))
                                        }
                                    >
                                        {defaultSelected.map((status) => (
                                            <MenuItem
                                                key={status.value}
                                                value={status.value}
                                            >
                                                {status.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.defaultSelected && (
                                <FormHelperText>
                                    {errors?.defaultSelected.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>

                        <FormControl
                            error={Boolean(errors?.isVisible)}
                            fullWidth
                            sx={{ ml: pxToRem(10) }}
                        >
                            <InputLabel>Is Visible</InputLabel>
                            <Controller
                                name="isVisible"
                                control={control}
                                rules={{
                                    required: 'Is required',
                                }}
                                render={({
                                    field: { name, value, onChange },
                                }) => (
                                    <Select
                                        label="Is Visible"
                                        name={name}
                                        value={value || ''}
                                        onChange={onChange}
                                    >
                                        {defaultSelected.map((status) => (
                                            <MenuItem
                                                key={status.value}
                                                value={status.value}
                                            >
                                                {status.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            {errors?.isVisible && (
                                <FormHelperText>
                                    {errors?.isVisible.message?.toString()}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    sx={{ width: '50%' }}
                    onClick={() => formRef.current?.requestSubmit()}
                    variant={'contained'}
                    loading={isLoading}
                >
                    Create Category
                </Button>
                <Button
                    sx={{ width: '50%', bgcolor: colors.action.menuSelect }}
                    onClick={() => closeModal()}
                    variant={'contained'}
                >
                    Cancel
                </Button>
            </DialogActions>

            <SuccessSnackbarEffect
                isSuccess={isSuccess}
                message={'Successfully Created an Card!'}
            />
        </>
    );
};
export default AddCategory;
